import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl, { StyleSpecification } from 'mapbox-gl';
import { pathToRegexp } from 'path-to-regexp';
import { useIntl } from '@alltrails/shared/react-intl';
import { additionalStyleCardConfigs, baseStyleCardConfigs } from '@alltrails/maps/utils/cardConfigs';
import { pointItemToLngLat } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import ActivityHoverCard from 'components/cards/ActivityHoverCard';
import CustomProvider from 'components/CustomProvider';
import baseStyleLoaders from 'utils/mapbox/baseStyleLoaders';
import type { Context } from 'types/Context';
import { BaseStyleId } from '@alltrails/maps/types/Styles';
import Waypoint from 'types/Waypoint';
import type Trail from 'types/Trails/Trail';
import Track from '@alltrails/shared/types/track';
import { List } from '@alltrails/shared/types/lists';
import type { PolylineMap } from '@alltrails/maps/types/PolylineMap';
import Radio from '@alltrails/shared/denali/components/Radio';
import Checkbox from '@alltrails/shared/denali/components/Checkbox';
import TrailHoverCard from '../../../../components/cards/TrailHoverCard';
import WaypointPopup from '../../../../components/shared/MapPopups/WaypointPopup';
import { MapElevationPaneWidget } from '../search/fullscreen/map_elevation_pane_widget';
import { fitMapToBounds } from '../../../../utils/mapbox/map_helpers';
import { addAtMap } from '../../../../utils/mapbox/overlays/at_map';
import { addExploreItems } from '../../../../utils/mapbox/overlays/explore_items';
import { updateCurrLocMarker } from '../../../../utils/mapbox/overlays/current_location';
import { initCustomStyle, swapBaseStyle, createBlankStyle } from '../../../../utils/mapbox/style_helpers';
import { LanguageSupportUtil } from '../../../../utils/language_support_util';
import { initLayerEvents } from '../../../../utils/mapbox/layers/layer_helpers';
import { additionalStyleLoaders } from '../../../../utils/mapbox/additionalStyleLoaders';

const WidgetElevationChart = lazy(() => import('@alltrails/maps/components/WidgetElevationChart'));

type Props = {
  alltrailsLink: string;
  atMap?: PolylineMap;
  context: Context & {
    locale: string;
  };
  list?: List;
  links?: { text: string; path: string }[];
  mapboxToken: string;
  mapLayerDefault: BaseStyleId;
  mapLayers?: string[];
  mapLayersTitle?: string;
  mapOverlays?: string[];
  mapOverlaysTitle?: string;
  scrollWheelZoom?: boolean;
  showElevationChart?: boolean;
  trail?: Trail;
};

const MapWidget = ({
  alltrailsLink,
  atMap,
  context,
  links,
  list,
  mapboxToken,
  mapLayerDefault,
  mapLayers,
  mapLayersTitle,
  mapOverlays,
  mapOverlaysTitle,
  scrollWheelZoom,
  showElevationChart,
  trail
}: Props) => {
  const mbMap = useRef(null);
  const [baseLayerKey, setBaseLayerKey] = useState(mapLayerDefault);
  const [showMenu, setShowMenu] = useState(false);
  const [overlaysOn, setOverlaysOn] = useState<Record<string, unknown>>({});
  const intl = useIntl();

  function renderWaypointPopup(waypoint: Waypoint) {
    // need to fully deserialize waypoint (which is partially serialized when converted to a mapbox property)
    if (typeof waypoint.location === 'string') {
      waypoint.location = JSON.parse(waypoint.location);
    }

    return (
      <CustomProvider>
        <WaypointPopup waypoint={waypoint} addToVRoute={null} />
      </CustomProvider>
    );
  }

  const renderTrailHoverCard = useCallback(
    (object: Trail) => {
      const handleCardClick = () => {
        window.open(LanguageSupportUtil.wrapUrlSafe(`/${object.slug}`, context.languageRegionCode));
      };
      return <TrailHoverCard context={context} handleCardClick={handleCardClick} handleFavoriteClick={handleCardClick} trail={object} />;
    },
    [context]
  );

  const renderTrackHoverCard = useCallback(
    (object: Track) => {
      // need to fully deserialize user (which is partially serialized when converted to a mapbox property)
      const { user } = object;
      if (typeof user === 'string') {
        object = { ...object };
        object.user = JSON.parse(user);
      }
      if (object?.estimated_time_to_complete && typeof object.estimated_time_to_complete === 'string') {
        object.estimated_time_to_complete = JSON.parse(object.estimated_time_to_complete);
      }
      const handleCardClick = () => {
        const url = `/explore/${object.type === 'track' ? 'recording' : 'map'}/${object.slug}`;
        window.open(LanguageSupportUtil.wrapUrlSafe(url, context.languageRegionCode));
      };
      return <ActivityHoverCard context={context} handleCardClick={handleCardClick} handleFavoriteClick={handleCardClick} track={object} />;
    },
    [context]
  );

  const renderHoverCard = useCallback(
    (object: Trail | Track) => {
      if (object.type === 'trail') {
        return renderTrailHoverCard(object as Trail);
      }
      if (object.type === 'track' || object.type === 'map') {
        return renderTrackHoverCard(object as Track);
      }
      return null;
    },
    [renderTrackHoverCard, renderTrailHoverCard]
  );

  const initLegacyMapWidget = useCallback(() => {
    if (!mapboxgl.supported()) {
      alert(
        intl.formatMessage({
          defaultMessage:
            'It appears that your browser does not support MapboxGL, a library that is required to render maps on AllTrails.com. Please upgrade or change browsers before reloading the page. See https://docs.mapbox.com/help/troubleshooting/mapbox-browser-support/ for more details. Contact customer support with details about your device and browser if issue persists.'
        })
      );
      return;
    }

    // Init mapbox map
    mapboxgl.accessToken = mapboxToken;
    const initCenter = pointItemToLngLat(trail || atMap) || undefined;
    const map = new mapboxgl.Map({
      center: initCenter,
      container: 'map',
      fadeDuration: 0,
      style: createBlankStyle() as StyleSpecification,
      zoom: 14
    }) as unknown as mapboxgl.Map;
    // Add custom AllTrails properties onto map
    (map as any).context = context;
    initCustomStyle(map);
    initLayerEvents(map);
    mbMap.current = map;

    if (!scrollWheelZoom) {
      mbMap.current.scrollZoom.disable();
    }

    // Switch to selected style
    mbMap.current.once('load', () => {
      const styleLoader = baseStyleLoaders[mapLayerDefault];
      // Load the style
      styleLoader(context.locale, context.displayMetric, intl).then(style => {
        // Set the style. Wait until we feel confident it is done loading.
        swapBaseStyle(map, style as StyleSpecification, false).then(() => {
          // Only run this code _after_ the style has been swapped to avoid issues with "style is not done loading"
          const bounds = new mapboxgl.LngLatBounds();
          if (list) {
            addExploreItems(map, 'list', list, (object: Trail | Track) => renderHoverCard(object), bounds);
          } else if (atMap) {
            const isMapCreatorPage = pathToRegexp('/:locale?/widget/map/:map').test(window?.location?.pathname) && !atMap?.originalAtMapId;
            addAtMap({
              atMap,
              bounds,
              handlePhotoClick: undefined,
              map,
              messagingChannel: undefined,
              renderWaypointPopup: (waypoint: Waypoint) => renderWaypointPopup(waypoint),
              polylineOpts: undefined,
              withWaypointLabels: false,
              isMapCreatorPage
            });
          }
          fitMapToBounds(map, bounds);
        });
      });
    });

    // Add map controls
    map.addControl(
      new mapboxgl.ScaleControl({
        maxWidth: 80,
        unit: context.displayMetric ? 'metric' : 'imperial'
      }) as unknown as mapboxgl.Control,
      'bottom-right'
    );
  }, [atMap, context, intl, list, mapLayerDefault, mapboxToken, renderHoverCard, scrollWheelZoom, trail]);

  const handleMouseOver = useCallback((position: unknown) => {
    // Map may be undefined if we hover over the elevation pane
    // before the map has finished loading.
    if (!mbMap.current) {
      return;
    }

    updateCurrLocMarker(mbMap.current, position);
  }, []);

  useEffect(() => {
    initLegacyMapWidget();
  }, [initLegacyMapWidget]);

  function toggleMenu() {
    setShowMenu(!showMenu);
  }

  function switchLayer(key: BaseStyleId) {
    const styleLoader = baseStyleLoaders[key];
    styleLoader(context.locale, context.displayMetric, intl).then((style: object) => {
      swapBaseStyle(mbMap.current, style, false);
    });
    setBaseLayerKey(key);
  }

  function toggleOverlay(key: keyof typeof additionalStyleLoaders) {
    const styleLoader = additionalStyleLoaders[key];

    const { [key]: overlay, ...otherOverlays } = overlaysOn;

    if (overlay) {
      styleLoader.remove(mbMap.current);
      setOverlaysOn(otherOverlays);
    } else {
      styleLoader.add(mbMap.current, { exploreMap: atMap });
      otherOverlays[key] = true;
      setOverlaysOn(otherOverlays);
    }
  }

  function renderLayerRadio(key: BaseStyleId, config: Record<string, { displayName: string }>) {
    const checked = baseLayerKey === key;
    return (
      <Radio
        selected={checked}
        value={key}
        size="sm"
        testId={`item-${key}`}
        onChange={() => switchLayer(key)}
        labelElement={<span>{config[key].displayName}</span>}
      />
    );
  }

  function renderOverlayCheckbox(key: keyof typeof additionalStyleLoaders, config: Record<string, { displayName: string }>) {
    return (
      <Checkbox
        id={key}
        size="sm"
        onChange={() => toggleOverlay(key)}
        labelElement={<span>{config[key].displayName}</span>}
        testId={`checkbox-item-${key}`}
        selected={!!overlaysOn[key]}
      />
    );
  }

  function renderMenu(
    title: string,
    keys: string[],
    config: Record<string, { displayName: string }>,
    formElementFunc: (key: string, config: Record<string, { displayName: string }>) => JSX.Element
  ) {
    if (keys.length < 1) {
      return null;
    }
    const menuItems = keys.map(key => <li key={`menu-item-li-${key}`}>{formElementFunc(key, config)}</li>);
    return (
      <>
        <div className="menu-title">{title}</div>
        <ul className="control">{menuItems}</ul>
      </>
    );
  }

  return (
    <div id="static-map">
      <div id="controls">
        <div id="titleBar">
          <a className="logo" href={alltrailsLink} target="_blank" rel="noopener">
            <img alt="AllTrails" src="https://cdn-assets.alltrails.com/assets/shared/alltrails_logo_widget.png" />
          </a>
          <div className="info-bar xlate-none">
            {links.flatMap((link, i) => [
              i > 0 ? (
                <span key={`${link.text} - delimiter`} className="delimiter">
                  {' '}
                  |{' '}
                </span>
              ) : null,
              <span key={link.text} className="info">
                <a href={link.path} target="_blank" rel="noopener">
                  {link.text}
                </a>
              </span>
            ])}
          </div>
        </div>
        <div id="map-controls">
          {showElevationChart && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {typeof window !== 'undefined' ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <WidgetElevationChart map={atMap} displayMetric={context.displayMetric} handleMouseOver={handleMouseOver} />
                </Suspense>
              ) : (
                <div id="elevation">
                  <MapElevationPaneWidget
                    collapsed={false}
                    displayMetric={context.displayMetric}
                    languageRegionCode={context.languageRegionCode}
                    elevationConfig={{
                      areaColor: 'white',
                      axisTextColor: 'gray',
                      backgroundColor: 'white',
                      endLineColor: 'blue',
                      hoverLineColor: '#219AD8',
                      lineColor: 'red',
                      textColor: 'gray',
                      tickColor: 'gray'
                    }}
                    handleMouseOver={handleMouseOver}
                    height={85}
                    map={atMap}
                    showStatsGroup={false}
                    width={255}
                  />
                </div>
              )}
            </>
          )}
          <div className="left zoom-group">
            <button id="zoom-in" className="zoom control" onClick={() => mbMap.current.zoomIn()} type="button">
              <span>
                <img alt="Zoom In" src="//cdn-assets.alltrails.com/assets/icons/maps/zoom-darkgreen.png" />
              </span>
            </button>
            <button id="zoom-out" className="zoom control" onClick={() => mbMap.current.zoomOut()} type="button">
              <span>
                <img alt="Zoom Out" src="//cdn-assets.alltrails.com/assets/icons/maps/unzoom-darkgreen.png" />
              </span>
            </button>
          </div>
          <div className="right control-group">
            <div className="map-control-container">
              <button className="flyout-button-container control" onClick={toggleMenu} type="button" data-testid="map-controls-button">
                <img alt="Map Options" className="layer-image" src="//cdn-assets.alltrails.com/assets/icons/trail/map-layers-dark-green.png" />
              </button>
              <div className={`${showMenu ? '' : 'hidden'} menu map-controls-menu`}>
                {renderMenu(mapLayersTitle, mapLayers, baseStyleCardConfigs, renderLayerRadio)}
                {renderMenu(mapOverlaysTitle, mapOverlays, additionalStyleCardConfigs, renderOverlayCheckbox)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div id="map" onClick={() => setShowMenu(false)} />
    </div>
  );
};

const MapWidgetWithProvider = (props: Props) => (
  <CustomProvider>
    <MapWidget {...props} />
  </CustomProvider>
);

export default MapWidgetWithProvider;
